import { useCallback, useMemo } from 'react';

import { ClientTransforms } from 'src/editor/transforms';
import { useCurrentUserId } from 'src/gql';
import { useAuthorizedTransforms } from 'src/hooks';
import { useEditor } from 'src/layout-engine';
import { useEditingDisabled, useEditingMode } from 'src/store/document/selectors';

import { acceptSpellcheckLint, ignoreSpellcheckLints } from './spellcheck-actions';
import { useSpellcheckTabId } from './spellcheck-state-registry';
import type { SpellcheckLint, SpellcheckSuggestion } from './spellcheck-vars';

/**
 * Shared by the in-document popover and the drawer, so the two cannot drift apart. The work itself
 * lives in `spellcheck-actions`; this only gathers what React knows and decides who may edit.
 */
export function useSpellcheckActions() {
  const editor = useEditor();
  const tabId = useSpellcheckTabId();
  const currentUserId = useCurrentUserId();
  const editingDisabled = useEditingDisabled();
  const { editingMode } = useEditingMode();
  const { AuthorizedTransforms } = useAuthorizedTransforms();

  const canReplace = currentUserId !== null && editingMode !== null && !editingDisabled;

  const context = useMemo(
    () => ({
      editor,
      focus: () => ClientTransforms.focus(editor),
      insertText: (text: string) => {
        if (currentUserId === null || editingMode === null) return;
        AuthorizedTransforms.insertTextAtSelection(editor, editingMode, currentUserId, text);
      },
      tabId,
    }),
    [AuthorizedTransforms, currentUserId, editingMode, editor, tabId]
  );

  const acceptLint = useCallback(
    (lint: SpellcheckLint, suggestion: SpellcheckSuggestion) => {
      if (!canReplace) return;
      acceptSpellcheckLint(context, lint, suggestion);
    },
    [canReplace, context]
  );

  const ignoreLints = useCallback(
    (lints: readonly SpellcheckLint[]) => ignoreSpellcheckLints(context, lints),
    [context]
  );

  return { acceptLint, canReplace, ignoreLints };
}
